// =============================================================================
// BASIC TYPE DEFINITIONS (to avoid circular dependencies)
// =============================================================================

export const OBJECT_FIELD_TYPES = {
  // data
  date: "date",
  dateTime: "datetime",

  boolean: "boolean",

  // numeric
  number: "number",
  integer: "integer",

  // string
  string: "string",
  email: "email",
  url: "url",
  phoneNumber: "phone_number",
  country: "country",
  owner: "owner",

  // array
  array: "array",
  badgeTags: "badge_tags"
} as const;

export const SUPPORTED_FIELDS_IN_REPORTING = [
  OBJECT_FIELD_TYPES.date,
  OBJECT_FIELD_TYPES.dateTime,
  OBJECT_FIELD_TYPES.boolean,
  OBJECT_FIELD_TYPES.number,
  OBJECT_FIELD_TYPES.integer,
  OBJECT_FIELD_TYPES.string,
  OBJECT_FIELD_TYPES.email,
  OBJECT_FIELD_TYPES.url,
  OBJECT_FIELD_TYPES.phoneNumber,
  OBJECT_FIELD_TYPES.country,
  OBJECT_FIELD_TYPES.owner,
  OBJECT_FIELD_TYPES.badgeTags
] as const;

export const OPERATORS_MAP = {
  equal: "exact",
  lessThan: "lt",
  lessThanEqual: "lte",
  greaterThan: "gt",
  greaterThanEqual: "gte",
  contains: "icontains",
  in: "in",
  notEqual: "not_exact",
  notContains: "not_icontains",
  notIn: "not_in",
  isSet: "is_set",
  isEmpty: "is_empty",
  isNull: "isnull"
} as const;

// =============================================================================
// DERIVED TYPES (based on constants above)
// =============================================================================

export type ObjectFilterOperator =
  (typeof OPERATORS_MAP)[keyof typeof OPERATORS_MAP];
export type ObjectFieldType =
  (typeof OBJECT_FIELD_TYPES)[keyof typeof OBJECT_FIELD_TYPES];

export type OperatorDetails = {
  label: string | React.ReactNode;
  value: ObjectFilterOperator;
  disableInput?: boolean;
};

// =============================================================================
// OPERATOR CONFIGURATION
// =============================================================================

const OPERATOR_DETAILS_MAP = {
  [OPERATORS_MAP.equal]: {
    label: "=",
    value: OPERATORS_MAP.equal
  },
  [OPERATORS_MAP.lessThan]: {
    label: "<",
    value: OPERATORS_MAP.lessThan
  },
  [OPERATORS_MAP.lessThanEqual]: {
    label: "<=",
    value: OPERATORS_MAP.lessThanEqual
  },
  [OPERATORS_MAP.greaterThan]: {
    label: ">",
    value: OPERATORS_MAP.greaterThan
  },
  [OPERATORS_MAP.greaterThanEqual]: {
    label: ">=",
    value: OPERATORS_MAP.greaterThanEqual
  },
  [OPERATORS_MAP.contains]: {
    label: "Contains",
    value: OPERATORS_MAP.contains
  },
  [OPERATORS_MAP.in]: {
    label: "In",
    value: OPERATORS_MAP.in
  },
  [OPERATORS_MAP.notEqual]: {
    label: "!=",
    value: OPERATORS_MAP.notEqual
  },
  [OPERATORS_MAP.notContains]: {
    label: "Does not contain",
    value: OPERATORS_MAP.notContains
  },
  [OPERATORS_MAP.notIn]: {
    label: "Not in",
    value: OPERATORS_MAP.notIn
  },
  [OPERATORS_MAP.isSet]: {
    label: "Has value",
    value: OPERATORS_MAP.isSet,
    disableInput: true
  },
  [OPERATORS_MAP.isEmpty]: {
    label: "Is Empty",
    value: OPERATORS_MAP.isEmpty,
    disableInput: true
  },
  [OPERATORS_MAP.isNull]: {
    label: "Is Null",
    value: OPERATORS_MAP.isNull,
    disableInput: true
  }
} as const satisfies Record<ObjectFilterOperator, OperatorDetails>;

// =============================================================================
// FIELD TYPE TO OPERATORS MAPPING
// =============================================================================

const DATE_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.notEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isSet],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isEmpty]
];

const BOOLEAN_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.notEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isSet],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isEmpty]
];

const NUMBER_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.notEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isSet],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isEmpty]
];

const STRING_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.notEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.notContains],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isSet],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isEmpty]
];

const DEFAULT_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.notEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.notContains],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isSet],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isEmpty]
] as const;

const BADGES_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains]
];

// =============================================================================
// PREDEFINED OPERATOR CONFIGURATIONS BY FIELD TYPE
// =============================================================================

/**
 * Default operators by field type - includes all available operators for each field type
 * This is the standard configuration used by Object Reports
 */
export const DEFAULT_OPERATORS_BY_FIELD_TYPE = {
  fieldTypes: {
    [OBJECT_FIELD_TYPES.date]: DATE_OPERATORS,
    [OBJECT_FIELD_TYPES.dateTime]: DATE_OPERATORS,
    [OBJECT_FIELD_TYPES.boolean]: BOOLEAN_OPERATORS,
    [OBJECT_FIELD_TYPES.number]: NUMBER_OPERATORS,
    [OBJECT_FIELD_TYPES.integer]: NUMBER_OPERATORS,
    [OBJECT_FIELD_TYPES.string]: STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.email]: STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.url]: STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.phoneNumber]: STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.country]: STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.owner]: STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.array]: STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.badgeTags]: BADGES_OPERATORS
  },
  defaultOperators: DEFAULT_OPERATORS
} as const;

// OS3 Search specific operator lists - clean whitelist approach
const OS3_SEARCH_DATE_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThanEqual]
];

const OS3_SEARCH_BOOLEAN_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal]
];

const OS3_SEARCH_NUMBER_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThanEqual]
];

const OS3_SEARCH_STRING_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains]
];

/**
 * OS3 Search operators by field type - clean whitelist of supported operators
 * Only includes operators that work well in search context
 */
export const OS3_SEARCH_OPERATORS_BY_FIELD_TYPE = {
  fieldTypes: {
    [OBJECT_FIELD_TYPES.date]: OS3_SEARCH_DATE_OPERATORS,
    [OBJECT_FIELD_TYPES.dateTime]: OS3_SEARCH_DATE_OPERATORS,
    [OBJECT_FIELD_TYPES.boolean]: OS3_SEARCH_BOOLEAN_OPERATORS,
    [OBJECT_FIELD_TYPES.number]: OS3_SEARCH_NUMBER_OPERATORS,
    [OBJECT_FIELD_TYPES.integer]: OS3_SEARCH_NUMBER_OPERATORS,
    [OBJECT_FIELD_TYPES.string]: OS3_SEARCH_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.email]: OS3_SEARCH_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.url]: OS3_SEARCH_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.phoneNumber]: OS3_SEARCH_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.country]: OS3_SEARCH_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.owner]: OS3_SEARCH_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.array]: OS3_SEARCH_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.badgeTags]: OS3_SEARCH_STRING_OPERATORS
  },
  // OS3 Search uses a more limited set of default operators
  defaultOperators: [
    OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
    OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains]
  ]
} as const;

// Survey Table specific operator lists - based on API documentation
const SURVEY_DATE_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThanEqual]
];

const SURVEY_NUMBER_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.greaterThanEqual],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThan],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.lessThanEqual]
];

const SURVEY_STRING_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isNull]
];

const SURVEY_BOOLEAN_OPERATORS = [
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
  OPERATOR_DETAILS_MAP[OPERATORS_MAP.isNull]
];

const SURVEY_ARRAY_OPERATORS = [OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains]];

/**
 * Survey Table operators by field type - matches API documentation
 * Supports operators: exact, gt, gte, lt, lte, in, contains, icontains for various field types
 */
export const SURVEY_OPERATORS_BY_FIELD_TYPE = {
  fieldTypes: {
    [OBJECT_FIELD_TYPES.date]: SURVEY_DATE_OPERATORS,
    [OBJECT_FIELD_TYPES.dateTime]: SURVEY_DATE_OPERATORS,
    [OBJECT_FIELD_TYPES.boolean]: SURVEY_BOOLEAN_OPERATORS,
    [OBJECT_FIELD_TYPES.number]: SURVEY_NUMBER_OPERATORS,
    [OBJECT_FIELD_TYPES.integer]: SURVEY_NUMBER_OPERATORS,
    [OBJECT_FIELD_TYPES.string]: SURVEY_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.email]: SURVEY_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.url]: SURVEY_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.phoneNumber]: SURVEY_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.country]: SURVEY_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.owner]: SURVEY_STRING_OPERATORS,
    [OBJECT_FIELD_TYPES.array]: SURVEY_ARRAY_OPERATORS,
    [OBJECT_FIELD_TYPES.badgeTags]: SURVEY_STRING_OPERATORS
  },
  defaultOperators: [
    OPERATOR_DETAILS_MAP[OPERATORS_MAP.equal],
    OPERATOR_DETAILS_MAP[OPERATORS_MAP.contains]
  ]
} as const;

// =============================================================================
// ADDITIONAL CONSTANTS FOR COMPATIBILITY
// =============================================================================

// All available operators
export const ALL_OPERATORS = Object.values(
  OPERATOR_DETAILS_MAP
) as OperatorDetails[];

// Field category labels
export const FIELD_CATEGORY_LABELS = {
  FIELDS: "Fields",
  ATTRIBUTES: "Attributes"
};

export const SMART_COLUMN_FIELDS = [
  {
    tag: "name",
    label: "Name",
    type: OBJECT_FIELD_TYPES.string
  },
  {
    tag: "created_at",
    label: "Created At",
    type: OBJECT_FIELD_TYPES.dateTime
  },
  {
    tag: "updated_at",
    label: "Updated At",
    type: OBJECT_FIELD_TYPES.dateTime
  },
  {
    tag: "uid",
    label: "UID",
    type: OBJECT_FIELD_TYPES.string
  },
  {
    tag: "badge_tags",
    label: "Badges",
    type: OBJECT_FIELD_TYPES.badgeTags
  }
] as const;
